import { useQuery } from "@apollo/client";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import {
  GET_EMBEDDED_DOCUMENT_PREPARATION_URL,
  GET_SUBMISSION_QUERY,
} from "queries/submissions";
import { useEffect, useRef, useState } from "react";
import { BOLDSIGN_ORIGIN, BoldSignData } from "./boldSignClientSideEvents";
import { useUpdateSignatureStatusToSent } from "hooks/useSubmissionMutations";
import { useAlerts } from "components/Alerts/AlertProvider";

interface EmbeddedDocumentPreparationUrlDialogProps {
  open: boolean;
  onClose: () => void;
  submissionId: string;
}

export function EmbeddedDocumentPreparationUrlDialog({
  open,
  onClose,
  submissionId,
}: EmbeddedDocumentPreparationUrlDialogProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const alerts = useAlerts();
  const [isSuccess, setIsSuccess] = useState(false);
  const [updateSignatureStatusToSent] = useUpdateSignatureStatusToSent();

  useEffect(() => {
    setIsSuccess(false);
  }, [open]);

  useEffect(() => {
    const onMessage = async (e: MessageEvent<BoldSignData>) => {
      if (e.origin !== BOLDSIGN_ORIGIN) return;
      if (!e.data) return;

      const action = typeof e.data === "string" ? e.data : e.data.action;

      if (action === "onCreateSuccess") {
        try {
          await updateSignatureStatusToSent({
            variables: { submissionId },
            refetchQueries: [GET_SUBMISSION_QUERY],
          });
          setIsSuccess(true);
        } catch (error) {
          console.error("Failed to update signature status to SENT", error);
          alerts.error(
            "We saved your changes, but updating the document status failed. Please try again."
          );
        } finally {
          onClose();
        }
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [alerts, onClose, updateSignatureStatusToSent, submissionId]);

  const { data, loading } = useQuery(GET_EMBEDDED_DOCUMENT_PREPARATION_URL, {
    variables: { submissionId },
    fetchPolicy: "network-only",
  });

  const preparationUrl = data?.getEmbeddedDocumentPreparationUrl;

  if (!loading && !preparationUrl) {
    return null;
  }

  if (isSuccess) {
    return (
      <Dialog
        title="Document Preparation"
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        noPrimaryAction
      >
        <Stack
          direction="row"
          spacing={1}
          justifyContent="center"
          alignItems="center"
          height="100%"
          pt={4}
          pb={8}
        >
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32 }} />
          <Typography>Document successfully prepared</Typography>
        </Stack>
      </Dialog>
    );
  }

  return (
    <Dialog
      title="Document Preparation"
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      sx={{ "& .MuiDialog-paper": { height: "90vh" } }}
      contentSx={{ height: "100%" }}
      DialogContentProps={{ sx: { p: 0 } }}
      noPrimaryAction
    >
      {loading && (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress />
        </Box>
      )}
      {preparationUrl && !isSuccess && (
        <iframe
          src={preparationUrl}
          ref={iframeRef}
          width="100%"
          height="100%"
          frameBorder="0"
        />
      )}
    </Dialog>
  );
}
